import { type JSX, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSession } from '@client/hooks/useSession';
import { ApiError } from '@client/api/client';
import { BrandMark } from '@ui/components/BrandMark';
import { Button } from '@ui/components/Button';
import { Spinner } from '@ui/components/Spinner';

export function AuthGate({ children }: { children?: ReactNode }): JSX.Element {
  const location = useLocation();
  const session = useSession();

  if (session.isLoading) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 text-slate-500">
        <BrandMark size="lg" />
        <Spinner size="md" label="Loading session" />
      </div>
    );
  }

  if (session.error instanceof ApiError && session.error.status === 401) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (session.error || !session.data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <BrandMark size="lg" />
        <p className="max-w-sm text-sm text-slate-600 dark:text-slate-300">
          We could not reach the server. Check your connection and try again.
        </p>
        <Button variant="secondary" onClick={() => session.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return <>{children ?? <Outlet />}</>;
}
